import * as React from 'react';
import * as TypeStyle from 'typestyle';
import { Messages } from '../../../gen-src/Localize';
import { ClickBustingConfig } from '../../config/ClickBusting';
import { encodeSvg } from '../../icons/Icons';
import { TextColors } from '../../styles/Colors';
import { PreventUserSelect } from '../../styles/PreventUserSelect';
import { TabStyles } from '../../styles/TabStyles';
import { makeTestId } from '../../utils/Declarative';

const ChipStyleName = TypeStyle.style(PreventUserSelect, {
  alignItems: 'center',
  backgroundColor: '#ebebeb',
  border: `1px solid #d4d4d4`,
  borderRadius: 1,
  boxSizing: 'border-box',
  color: TextColors.Primary,
  display: 'inline-flex',
  fontFamily: 'inherit',
  fontSize: TabStyles.Typography.Sizing.Default,
  lineHeight: 'inherit',
  overflow: 'hidden',
  padding: '0 3px',
  position: 'relative',
  transition: 'border-color 75ms',
  $nest: {
    '&:hover': { backgroundColor: '#f5f5f5' },
    '&[aria-selected="true"]': { backgroundColor: '#cccccc' },
    '&:focus': {
      borderColor: TabStyles.Colors.Focus,
      boxShadow: 'none !important', // unfortunate VizClient-specific override for TFS 1001129
      outline: 'none',
    },
    '&:active': { backgroundColor: '#d4d4d4' },
    '&[aria-disabled="true"]': { backgroundColor: 'transparent !important', color: `${TextColors.Disabled} !important` },
    '&.pending::after': {
      backgroundColor: 'rgba(255,255,255,0.6)',
      bottom: 0,
      content: `""`,
      left: 0,
      pointerEvents: 'none',
      position: 'absolute',
      right: 0,
      top: 0,
    }
  }
});

export interface ChipProps {
  /** The rendered contents of this chip. */
  children: React.ReactNode;

  /** Show a Remove button that invokes this callback, if provided. */
  onRemove?: () => void;

  /**
   * Renders the chip with a "pending" visual style.
   * Typically used to indicate some sort of asynchronous validation is occurring.
   */
  pending?: boolean;

  testId?: string;

  /**
   * The tooltip of the chip (excluding the Remove button). n.b. implementation details of the tooltip are subject to change;
   * the contract of this prop is only that a tooltip will somehow be displayed.
   */
  tooltip?: string;

  /** The unique value of this chip. */
  value: string;
}

// tslint:disable:max-line-length
const RemoveChipIcon = (fill: string = '#666') => `<svg xmlns="http://www.w3.org/2000/svg" width="8" height="8">
<path fill="${fill}" d="M4.78873239 4L7.83098592.95774648c.22535211-.22535211.22535211-.56338028 0-.7887324-.22535212-.2253521-.56338029-.2253521-.7887324 0L4 3.21126762.95774648.16901408c-.22535211-.2253521-.56338028-.2253521-.7887324 0-.2253521.22535212-.2253521.56338029 0 .7887324L3.21126762 4 .16901408 7.04225352c-.2253521.22535211-.2253521.56338028 0 .7887324.22535212.22535211.56338029.22535211.7887324 0L4 4.78873239l3.04225352 3.04225353c.22535211.22535211.56338028.22535211.7887324 0 .22535211-.22535212.22535211-.56338029 0-.7887324L4.78873239 4z"/>
</svg>`;
// tslint:enable:max-line-length

const RemoveButtonStyleName = TypeStyle.style({
  background: `center no-repeat url(${encodeSvg(RemoveChipIcon())})`,
  boxSizing: 'border-box',
  flexShrink: 0,
  height: 22,
  marginLeft: 5,
  marginRight: -3,
  width: 22,
  $nest: {
    '&:hover': { backgroundImage: `url(${encodeSvg(RemoveChipIcon('#333'))})` },
    '&:active': { backgroundImage: `url(${encodeSvg(RemoveChipIcon('#333'))})` }
  }
});

const RemoveButton: React.FunctionComponent<Pick<ChipProps, 'onRemove'>> = props => <div
  aria-hidden={true}
  className={RemoveButtonStyleName}
  data-tb-test-id='remove-Button'
  onClick={props.onRemove && (e => { props.onRemove?.(); e.stopPropagation(); })}
  role='button'
  title={Messages.remove()}
/>;

export const Chip = React.forwardRef<HTMLDivElement, ChipProps & React.HTMLAttributes<HTMLElement>>((props, ref) => {
  const { className, children, onRemove, pending, testId, tooltip, value, ...htmlProps } = props;

  return (
    <div
      data-tb-test-id={makeTestId(testId!, 'Chip')}
      ref={ref}
      title={tooltip} // can be overridden by setting `title` prop
      {...htmlProps}
      className={TypeStyle.classes(ChipStyleName, ClickBustingConfig.suppressionClassName, { pending }, className)}
    >
      {children}
      {onRemove && <RemoveButton onRemove={onRemove} />}
    </div>
  );
});
Chip.displayName = 'Chip';
